import {TranslateService} from '@ngx-translate/core';
import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ModeEnum, ProductTypeEnum, Remuneration, ProviderDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService} from 'primeng/api';
import {
    ProductRemunerationSelectorComponent
} from '../product-remuneration-selector/product-remuneration-selector.component';
import {RemunerationTransactionType} from '../remuneration-configuration';
import {showValidationError} from '../../form-validation';

type RemunerationWithFilters = Remuneration & {
    typeTransaction?: RemunerationTransactionType;
    flightDepartureCity?: string | null;
    flightDepartureAirport?: string | null;
    flightArrivalCity?: string | null;
    flightArrivalAirport?: string | null;
};

@Component({
    selector: 'dmp-remuneration-accordion',
    templateUrl: './remuneration-accordion.component.html',
    styleUrls: ['./remuneration-accordion.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class RemunerationAccordionComponent implements OnChanges {
    @Input() remunerations!: FormArray;
    @Input() datastore: string | null = null;
    @Input() readOnly = false;
    @Input() submitted = false;
    @Input() fixedProvider = false;
    @Input() transactionType: RemunerationTransactionType = 'command';
    providerConfigurationError = '';
    private static readonly groupProviders = new WeakMap<FormGroup, string | null>();

    paymentsMethodOptions: Array<{ label: string; value: string; disabled?: boolean }> = [];
    activeRemunerationIndex: number | null = null;

    protected readonly productTypeLabels: Record<string, string> = {
        [ProductTypeEnum.FLIGHT]: this.translate.instant('invoicing.common.flight'),
        [ProductTypeEnum.HOTEL]: this.translate.instant('invoicing.common.hotel'),
        [ProductTypeEnum.CAR]: this.translate.instant('invoicing.common.car'),
        [ProductTypeEnum.PARKING]: this.translate.instant('invoicing.common.parking')
    };

    protected readonly ProductTypeEnum = ProductTypeEnum;

    constructor(private readonly translate: TranslateService, private fb: FormBuilder, private confirmationService: ConfirmationService) {
        this.paymentsMethodOptions = [{
            label: '...',
            value: '',
        }, {
            label: this.translate.instant('invoicing.remuneration.card'), value: 'cb', disabled: false
        }];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.remunerations) {
            this.normalizeActiveRemunerationIndex();
        }
        if (changes.submitted?.currentValue) {
            const firstInvalid = this.remunerations?.controls.findIndex(control => control.invalid);
            if (firstInvalid >= 0) {
                this.activeRemunerationIndex = firstInvalid;
            }
        }
    }

    isInvalid(control: AbstractControl | null): boolean {
        return showValidationError(control, this.submitted);
    }

    public get companyProviderRows(): Array<{ index: number; control: AbstractControl }> {
        if (!this.remunerations) {
            return [];
        }
        return this.remunerations.controls.map((control, index) => ({index, control}));
    }

    public static buildCompanyProviderGroup(
        fb: FormBuilder,
        provider: string | null = null,
        remunerations: RemunerationWithFilters[] = [],
        productTypes?: ProductTypeEnum[],
        transactionType: RemunerationTransactionType = 'command'
    ): FormGroup {
        // Each client owns its rules, including nested geographic filters.
        remunerations = JSON.parse(JSON.stringify(remunerations.filter(rule => rule.typeTransaction === transactionType)));
        const selectedProducts = productTypes ?? Array.from(new Set(remunerations.map(rule => rule.productType)));
        const products = selectedProducts.map(productType => {
            const productRemunerations = remunerations.filter(rule => rule.productType === productType);
            const defaultRule = productRemunerations.find(rule => rule.defaultRemuneration === true)
                || productRemunerations.find(rule =>
                    !RemunerationAccordionComponent.hasFlightFilters(rule) && !rule.paymentMethod
                    && !rule.startDate && !rule.endDate
                );
            const overrides = productRemunerations.filter(rule => rule !== defaultRule);
            const normalizedDefault = {
                ...(defaultRule || {}),
                provider,
                productType,
                typeTransaction: transactionType,
                defaultRemuneration: true
            } as RemunerationWithFilters;

            return fb.group({
                productType: [productType],
                defaultRemuneration: [fb.group(
                    ProductRemunerationSelectorComponent.initFormGroup(normalizedDefault, provider || undefined)
                )],
                overrides: fb.array(overrides.map(rule => RemunerationAccordionComponent.buildRuleGroup(
                    fb,
                    {...rule, provider, productType, typeTransaction: transactionType,
                        defaultRemuneration: false} as RemunerationWithFilters
                )))
            });
        });

        const providerGroup = fb.group({
            provider: [provider, Validators.required],
            products: fb.array(products, transactionType === 'command' ? Validators.required : [])
        });
        RemunerationAccordionComponent.groupProviders.set(providerGroup, provider);
        return providerGroup;
    }

    public selectCompanyProvider(control: AbstractControl, provider: ProviderDTO | null): void {
        const group = control as FormGroup;
        const previous = RemunerationAccordionComponent.groupProviders.get(group) || null;
        // Restore immediately while the user decides whether to replace an existing agreement.
        group.get('provider')?.setValue(previous, {emitEvent: false});
        if ((provider?.code || null) === previous) {
            return;
        }
        this.providerConfigurationError = '';
        const matchingRules = (provider?.remunerations || []).filter(rule =>
            (rule as RemunerationWithFilters).typeTransaction === this.transactionType);
        const productTypes = this.transactionType === 'command'
            ? provider?.products || []
            : Array.from(new Set(matchingRules.map(rule => rule.productType)));
        if (!productTypes.length) {
            this.providerConfigurationError = this.transactionType === 'lead'
                ? this.translate.instant('invoicing.remuneration.noLeadRemuneration')
                : this.translate.instant('invoicing.remuneration.configureProvider');
            return;
        }
        const apply = () => {
            const replacement = RemunerationAccordionComponent.buildCompanyProviderGroup(
                this.fb, provider.code, matchingRules, productTypes, this.transactionType);
            group.setControl('products', replacement.get('products'));
            group.get('provider')?.setValue(provider.code);
            group.markAsDirty();
            RemunerationAccordionComponent.groupProviders.set(group, provider.code);
        };
        if (previous) {
            this.confirmationService.confirm({
                key: 'replace-provider', header: this.translate.instant('invoicing.remuneration.changeProvider'),
                message: this.translate.instant('invoicing.remuneration.replaceProvider'),
                acceptLabel: 'Remplacer', rejectLabel: this.translate.instant('invoicing.common.cancel'), accept: apply
            });
        } else {
            apply();
        }
    }

    private static buildRuleGroup(fb: FormBuilder, remuneration: RemunerationWithFilters): FormGroup {
        return fb.group({
            remuneration: [fb.group(ProductRemunerationSelectorComponent.initFormGroup(remuneration))]
        });
    }

    public static updateProviderOnRules(providerGroup: FormGroup, provider: string | null): void {
        const products = providerGroup.get('products') as FormArray;
        products?.controls.forEach(productControl => {
            const product = productControl as FormGroup;
            const defaultGroup = product.get('defaultRemuneration')?.value;
            if (defaultGroup instanceof FormGroup) {
                defaultGroup.get('provider')?.setValue(provider);
            }
            const overrides = product.get('overrides') as FormArray;
            overrides?.controls.forEach(overrideControl => {
                const overrideGroup = (overrideControl as FormGroup).get('remuneration')?.value;
                if (overrideGroup instanceof FormGroup) {
                    overrideGroup.get('provider')?.setValue(provider);
                }
            });
        });
    }

    private static hasFlightFilters(remuneration: RemunerationWithFilters): boolean {
        return !!(
            remuneration.flightMarketingCarriers?.length
            || remuneration.flightDepartureCities?.length
            || remuneration.flightDepartureAirports?.length
            || remuneration.flightDepartureCountries?.length
            || remuneration.flightArrivalCities?.length
            || remuneration.flightArrivalAirports?.length
            || remuneration.flightArrivalCountries?.length
            || (remuneration as any).flightDepartureCity
            || (remuneration as any).flightDepartureAirport
            || (remuneration as any).flightArrivalCity
            || (remuneration as any).flightArrivalAirport
        );
    }

    public addCompanyProvider(): void {
        if (this.readOnly || !this.remunerations
            || (this.remunerations.length > 0 && this.remunerations.invalid)) {
            this.remunerations?.markAllAsTouched();
            return;
        }
        this.remunerations.push(RemunerationAccordionComponent.buildCompanyProviderGroup(
            this.fb, null, [], undefined, this.transactionType));
        this.activeRemunerationIndex = this.remunerations.length - 1;
    }

    public removeCompanyProvider(index: number): void {
        if (this.readOnly || !this.remunerations) {
            return;
        }
        this.remunerations.removeAt(index);
        if (this.activeRemunerationIndex != null && this.activeRemunerationIndex > index) {
            this.activeRemunerationIndex--;
        }
        this.normalizeActiveRemunerationIndex();
    }

    public getCompanyProviderGroup(control: AbstractControl): FormGroup {
        return control as FormGroup;
    }

    public getCompanyProducts(control: AbstractControl): AbstractControl[] {
        return ((control as FormGroup).get('products') as FormArray)?.controls || [];
    }

    public getCompanyProductGroup(control: AbstractControl): FormGroup {
        return control as FormGroup;
    }

    public getProductOverrides(control: AbstractControl): FormArray {
        return (control as FormGroup).get('overrides') as FormArray;
    }

    public addCompanyOverride(providerControl: AbstractControl, productControl: AbstractControl): void {
        if (this.readOnly) {
            return;
        }
        const provider = (providerControl as FormGroup).get('provider')?.value;
        const productType = (productControl as FormGroup).get('productType')?.value as ProductTypeEnum;
        const remuneration = {
            provider,
            productType,
            typeTransaction: this.transactionType,
            mode: this.transactionType === 'lead' ? ModeEnum.FIXED : ModeEnum.PERCENTAGE,
            paymentMethod: null,
            defaultRemuneration: false,
            flightMarketingCarriers: [],
            flightDepartureCities: [],
            flightDepartureCountries: [],
            flightDepartureAirports: [],
            flightArrivalCities: [],
            flightArrivalCountries: [],
            flightArrivalAirports: []
        } as RemunerationWithFilters;
        this.getProductOverrides(productControl).push(
            RemunerationAccordionComponent.buildRuleGroup(this.fb, remuneration)
        );
    }

    public removeCompanyOverride(productControl: AbstractControl, index: number): void {
        if (!this.readOnly) {
            this.getProductOverrides(productControl).removeAt(index);
        }
    }

    public moveCompanyOverride(productControl: AbstractControl, index: number, direction: -1 | 1): void {
        const overrides = this.getProductOverrides(productControl);
        const destination = index + direction;
        if (this.readOnly || overrides.disabled || index < 0 || index >= overrides.length
            || destination < 0 || destination >= overrides.length) {
            return;
        }
        const rule = overrides.at(index);
        overrides.removeAt(index, {emitEvent: false});
        overrides.insert(destination, rule, {emitEvent: false});
        overrides.markAsDirty();
        overrides.updateValueAndValidity();
    }

    public trackByOverrideControl(_index: number, control: AbstractControl): AbstractControl {
        return control;
    }

    public companyProviderSummary(control: AbstractControl, index: number): string {
        return (control as FormGroup).get('provider')?.value || this.translate.instant('invoicing.remuneration.newProvider', {index: index + 1});
    }

    public companyProductLabel(control: AbstractControl): string {
        const productType = (control as FormGroup).get('productType')?.value;
        return this.productTypeLabels[productType] || productType;
    }

    public canAddCompanyProvider(): boolean {
        return !this.readOnly
            && !!this.remunerations
            && (this.remunerations.length === 0 || !this.remunerations.invalid);
    }

    public trackByRemunerationControl(
        _displayIndex: number,
        remunerationRow: { index: number; control: AbstractControl }
    ): AbstractControl {
        return remunerationRow.control;
    }

    public onRemunerationAccordionValueChange(value: number | string | Array<number | string> | null): void {
        if (value == null || value === '') {
            this.activeRemunerationIndex = null;
            return;
        }
        const rawValue = Array.isArray(value) ? value[0] : value;
        const parsedValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        this.activeRemunerationIndex = Number.isFinite(parsedValue) ? parsedValue : null;
        this.normalizeActiveRemunerationIndex();
    }

    private normalizeActiveRemunerationIndex(): void {
        if (!this.remunerations || this.remunerations.length === 0) {
            this.activeRemunerationIndex = null;
            return;
        }
        if (this.activeRemunerationIndex == null || this.activeRemunerationIndex < 0) {
            this.activeRemunerationIndex = 0;
            return;
        }
        if (this.activeRemunerationIndex >= this.remunerations.length) {
            this.activeRemunerationIndex = this.remunerations.length - 1;
        }
    }
}
